import { useEffect, useRef, useState } from 'react'

import { MatterBriefcase, TrashDelete } from '@/assets/icons'
import { Modal, ModalContent, ModalFooterWithActions, ModalFooterWithActionsType } from '@/components/modal'

export type LeaveOrganizationModalProps = {
  /** Whether the modal is open. */
  visible: boolean
  /** Name of the organization being left; also the value the user must type to confirm. */
  organizationName: string
  /** Called when the user dismisses the modal (Cancel, Escape, or outside click). */
  onClose: () => void
  /** Called when the user confirms leaving after entering the organization name. */
  onConfirm: () => void
  /** Drives the disabled and loading states while the parent performs the leave request. */
  isLeaving?: boolean
  /** Request failure supplied by the parent orchestration layer. */
  error?: string | null
  /** Raise this modal above another open modal, such as the member roster. */
  stacked?: boolean
}

const kLeaveWarnings = [
  {
    icon: <MatterBriefcase aria-hidden className="size-[18px] text-brand-neutral-600" />,
    text: 'Your matters transfer to an admin, including any created before you joined. They will not transfer back to you.',
  },
  {
    icon: <TrashDelete aria-hidden className="size-[18px] text-brand-neutral-600" />,
    text: 'Your AI chats and drafts are permanently deleted. This can’t be undone.',
  },
]

/**
 * Presentation-only confirmation modal for leaving an organization. The parent owns request state
 * and orchestration; this component owns only the type-to-confirm field.
 */
export default function LeaveOrganizationModal({
  visible,
  organizationName,
  onClose,
  onConfirm,
  isLeaving = false,
  error,
  stacked = false,
}: LeaveOrganizationModalProps) {
  const [confirmName, setConfirmName] = useState('')
  const formRef = useRef<HTMLFormElement>(null)
  const isMatch = confirmName.trim() === organizationName.trim()
  const canConfirm = isMatch && !isLeaving

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (canConfirm) onConfirm()
  }

  // The component stays mounted while `visible` toggles, so clear the confirmation field on close
  // to re-arm the type-to-confirm gate when the modal reopens.
  useEffect(() => {
    if (!visible) setConfirmName('')
  }, [visible])

  return (
    <Modal open={visible} onOpenChange={(open) => !open && !isLeaving && onClose()}>
      <ModalContent
        title="Leave organization"
        subtitle={organizationName}
        subtitleClassName="font-semibold text-brand-neutral-600"
        width="md"
        height="max-lg"
        className="max-w-[568px]"
        bodyClassName="-mt-1 font-sans"
        preventDismiss={isLeaving}
        stacked={stacked}
        footer={
          <ModalFooterWithActions
            type={ModalFooterWithActionsType.DESTRUCTIVE}
            inlineMessage={error ? { variant: 'error', children: error } : undefined}
            secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: isLeaving }}
            primaryAction={{
              label: 'Leave Organization',
              onClick: () => formRef.current?.requestSubmit(),
              disabled: !canConfirm,
              loading: isLeaving,
            }}
          />
        }
      >
        <p className="text-base font-normal leading-6 tracking-[-0.32px] text-brand-neutral-950">
          You’ll lose access immediately. Leaving also affects your work:
        </p>

        <ul className="mt-5 flex flex-col gap-5">
          {kLeaveWarnings.map((warning) => (
            <li key={warning.text} className="flex items-start gap-1.5">
              <span className="flex size-6 shrink-0 items-center justify-center">{warning.icon}</span>
              <span className="text-base leading-6 tracking-[-0.32px] text-brand-neutral-950">{warning.text}</span>
            </li>
          ))}
        </ul>

        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="mt-6">
            <label htmlFor="leave-org-confirm" className="mb-2 block text-sm font-normal leading-5 tracking-[-0.15px] text-brand-neutral-600">
              Type the organization name to confirm
            </label>
            <input
              id="leave-org-confirm"
              type="text"
              value={confirmName}
              onChange={(event) => setConfirmName(event.target.value)}
              placeholder={organizationName}
              autoComplete="off"
              disabled={isLeaving}
              className="block min-h-11 w-full rounded border-0 bg-white px-3 py-2.5 text-base leading-6 tracking-[-0.32px] text-brand-neutral-950 placeholder:text-brand-neutral-600 ring-1 ring-inset ring-brand-neutral-400 focus:outline-hidden focus:ring-inset focus:ring-brand-400 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </form>
      </ModalContent>
    </Modal>
  )
}
